import type { ReactNode } from 'react';
import { ChevronDown, ChevronUp, Eye, EyeOff, Loader2 } from 'lucide-react';

// Field shape used by the credential-input lookup tables in both edit dialogs.
export type CredField = {
  label: string;
  key: string;
  type: string;
  placeholder: string;
  required?: boolean;
  hint?: string;
};

// Solar inverter control (curtailment) — shared by the user and admin edit
// dialogs. Selecting an inverter stores sites.solar_control_adapter; its
// credentials go to sites.solar_credentials. Distinct from the solar
// *forecast* provider and from solar_config (capacity/brand metadata).
export const SOLAR_INVERTERS: { value: string; label: string; comingSoon: boolean }[] = [
  { value: '', label: 'None (no inverter control)', comingSoon: false },
  { value: 'enphase', label: 'Enphase (Envoy)', comingSoon: false },
  { value: 'goodwe', label: 'GoodWe', comingSoon: true },
  { value: 'alpha_ess', label: 'AlphaESS', comingSoon: true },
];

export const SOLAR_CONTROL_FIELDS: Record<string, CredField[]> = {
  enphase: [
    {
      label: 'Enlighten Email',
      key: 'enlighten_username',
      type: 'text',
      placeholder: 'Enphase Enlighten account email',
      required: true,
      hint: 'Must be an Enphase installer (or DIY-installer) account with access to this system.',
    },
    {
      label: 'Enlighten Password',
      key: 'enlighten_password',
      type: 'password',
      placeholder: 'Enphase Enlighten account password',
      required: true,
    },
    {
      label: 'Enphase System ID',
      key: 'system_id',
      type: 'text',
      placeholder: 'e.g. 1717812',
      required: true,
      hint: 'The System ID shown in the Enphase app, or the number in the enlighten.enphaseenergy.com/systems/<id> URL.',
    },
  ],
};

interface SectionHeaderProps {
  title: string;
  icon: ReactNode;
  isOpen: boolean;
  onToggle: () => void;
}

/**
 * Collapsible accordion header used by the Edit Site dialog (admin and user
 * variants). Renders a clickable bar showing the section title and a
 * chevron that flips with ``isOpen``.
 */
export function SectionHeader({ title, icon, isOpen, onToggle }: SectionHeaderProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-left text-sm font-medium text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800"
    >
      <span className="flex items-center gap-2">
        {icon}
        {title}
      </span>
      {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
    </button>
  );
}

interface CredentialFieldProps {
  field: CredField;
  /** Namespace baked into the input ``name`` so browsers can't pattern-match the field. */
  formNamespace: string;
  value: string;
  onChange: (value: string) => void;
  editing: boolean;
  revealed: boolean;
  onToggleReveal: () => void;
  credsLoading: boolean;
  currentValue: unknown;
  formatCredValue: (value: unknown) => string;
}

/**
 * Credential input + "Show current" reveal.
 *
 * Browser autofill notes:
 *  - The wrapping ``<form autoComplete="off">`` is the first line of defence.
 *  - Password fields additionally use ``autoComplete="new-password"`` — the
 *    only signal Chrome reliably honours to skip its "your saved login"
 *    autofill prompt.
 *  - Each input is given a unique namespaced ``name`` so the browser cannot
 *    pattern-match it to a saved app credential.
 *
 * The "Show current" toggle lazily reveals the stored value (fetched once
 * per dialog open via ``getSiteCredentials``). The input itself stays
 * empty so the existing "save without changes preserves stored values"
 * behaviour keeps working.
 */
export function CredentialField({
  field,
  formNamespace,
  value,
  onChange,
  editing,
  revealed,
  onToggleReveal,
  credsLoading,
  currentValue,
  formatCredValue,
}: CredentialFieldProps) {
  const isPassword = field.type === 'password';
  const inputName = `solaniq-${formNamespace}-${field.key}`;
  const hasStoredValue =
    currentValue !== undefined && currentValue !== null && currentValue !== '';

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {field.label} {field.required && <span className="text-red-500">*</span>}
        </label>
        {editing && (
          <button
            type="button"
            onClick={onToggleReveal}
            disabled={credsLoading}
            className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-50"
            title={revealed ? 'Hide current value' : 'Show current value'}
          >
            {credsLoading && !revealed ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : revealed ? (
              <EyeOff className="w-3 h-3" />
            ) : (
              <Eye className="w-3 h-3" />
            )}
            {revealed ? 'Hide current' : 'Show current'}
          </button>
        )}
      </div>
      <input
        type={field.type}
        name={inputName}
        autoComplete={isPassword ? 'new-password' : 'off'}
        data-form-type="other"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
        placeholder={field.placeholder}
      />
      {field.hint && (
        <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">{field.hint}</p>
      )}
      {editing && revealed && (
        <p className="mt-1 text-xs font-mono text-gray-700 dark:text-gray-300 break-all">
          {hasStoredValue ? `Currently: ${formatCredValue(currentValue)}` : 'Currently: (not set)'}
        </p>
      )}
    </div>
  );
}

export const formatCredValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '(not set)';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
};
